import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { useModals, Modal, ModalProps, Box, Button, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface ChoosePeriodModalProps extends ModalProps {
  dateFrom?: Date;
  dateTo?: Date;
  // todayOrLaterIsRequired?: boolean;
  onApply: (dateFrom: Date, dateTo: Date) => void;
}

export function ChoosePeriodModal({
  dateFrom,
  dateTo,
  // todayOrLaterIsRequired = false,
  onApply,
  ...props
}: ChoosePeriodModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const periodSchema = z
    .object({
      dateFrom: z.date({ error: t("errors:field_required") }),
      dateTo: z.date({ error: t("errors:field_required") }),
    })
    .superRefine((data, ctx) => {
      if (!data.dateFrom || !data.dateTo) return;

      const startUtc = new Date(data.dateFrom.getTime());
      startUtc.setUTCHours(0, 0, 0, 0);
      const endUtc = new Date(data.dateTo.getTime());
      endUtc.setUTCHours(0, 0, 0, 0);

      if (endUtc < startUtc) {
        ctx.addIssue({
          code: "custom",
          message: t("errors:end_date_should_be_greater_start_date"),
          path: ["dateTo"],
        });
      }
    });

  type PeriodSchema = z.infer<typeof periodSchema>;

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<PeriodSchema>({
    mode: "onSubmit",
    resolver: zodResolver(periodSchema),
    defaultValues: {
      dateFrom: dateFrom,
      dateTo: dateTo,
    },
  });

  const selectedFrom = useWatch({ control, name: "dateFrom" });
  const selectedTo = useWatch({ control, name: "dateTo" });

  const toInputRef = useRef<HTMLInputElement>(null);

  const onSubmit = handleSubmit((schemaData) => {
    onApply(schemaData.dateFrom, schemaData.dateTo);
    close(id);
  });

  return (
    <>
      <Modal
        {...props}
        title={t("common:period")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <Button type={"submit"} size={"lg"} onClick={onSubmit}>
              {t("common:save")}
            </Button>
            <Button
              type={"submit"}
              size={"lg"}
              variant={"plain"}
              onClick={() => {
                close(id);
              }}
            >
              {t("common:cancel")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Box className={"grid grid-cols-2 gap-sm"}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <Datepicker
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    if (!date) return;
                    if (!selectedTo || date > selectedTo) {
                      setValue("dateTo", date);
                      requestAnimationFrame(() => toInputRef.current?.focus());
                    }
                  }}
                  label={t("common:date_from")}
                  error={errors.dateFrom?.message}
                />
              )}
              control={control}
              name={"dateFrom"}
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Datepicker
                  value={value}
                  onChange={onChange}
                  label={t("common:date_to")}
                  minDate={selectedFrom}
                  openToDate={selectedFrom}
                  inputRef={toInputRef}
                  error={errors.dateTo?.message}
                />
              )}
              control={control}
              name={"dateTo"}
            />
          </Box>
        </form>
      </Modal>
    </>
  );
}
